import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { InLieuOibPossibilities } from "helpers/RedirectHelper";

export interface User extends InLieuOibPossibilities {
    userId: string;
    email: string;
    accessToken: string;
    refreshToken: string;
    role: { value: number; label: string };
    navToken?: string;
    messageId?: string;
    oib?: string;
    toLegalIzvorReg?: string;
    forLegalIzvorReg?: string;
    ePoljoprivredaGuid?: string;
    previousLogin?: string;
    isAdLogin?: boolean;
    showChangeEntityDialog: boolean;
}

export interface SignupNiasData {
    navToken?: string;
    messageId?: string;
    forPersonOib?: string;
    forLegalIps?: string;
    toLegalIps?: string;
    toLegalIzvorReg?: string;
    forLegalIzvorReg?: string;
    oib?: string;
}

interface State {
    isLoading: boolean;
    signInError: boolean;
    isRenewing: boolean;
    errorMessage: string;
    signInSuccess: boolean;
    user: User | null;
    signupNiasData: SignupNiasData | null;
}

export interface SignInSuccess extends User {}

interface RefreshTokenSuccess {
    accessToken: string;
    refreshToken: string;
}

interface SignInError {
    errorMessage: string;
}

interface ChangeShowChangeEntityDialog {
    showChangeEntityDialog: boolean;
}

const initialState: State = {
    isLoading: false,
    isRenewing: false,
    signInError: false,
    signInSuccess: false,
    errorMessage: "",
    user: null,
    signupNiasData: null,
};

const signInSlice = createSlice({
    name: "SignIn",
    initialState: initialState,
    reducers: {
        signInRequest: state => {
            state.signInError = false;
            state.signInSuccess = false;
            state.isLoading = true;
        },
        signInSuccess: (state, action: PayloadAction<SignInSuccess>) => {
            state.isLoading = false;
            state.signInSuccess = true;
            state.user = { ...action.payload };
        },
        signInDone: state => {
            state.isLoading = false;
            state.signInSuccess = true;
        },
        signInError: (state, action: PayloadAction<SignInError>) => {
            state.isLoading = false;
            state.signInError = true;
            state.errorMessage = action.payload.errorMessage;
            state.user = null;
        },
        signOut: state => {
            state.user = null;
        },
        tokenRenewRequest: state => {
            state.isRenewing = true;
        },
        tokenRenewSuccess: (state, action: PayloadAction<RefreshTokenSuccess>) => {
            state.isRenewing = false;

            if (state.user) {
                state.user.accessToken = action.payload.accessToken;
                state.user.refreshToken = action.payload.refreshToken;
            }
        },
        tokenRenewError: state => {
            state.isRenewing = false;
            state.user = null;
        },
        changeShowChangeEntityDialog: (
            state,
            action: PayloadAction<ChangeShowChangeEntityDialog>
        ) => {
            if (!state.user) return;
            state.user.showChangeEntityDialog = action.payload.showChangeEntityDialog;
        },
        setSignupNiasData: (state, action: PayloadAction<SignupNiasData>) => {
            state.signupNiasData = action.payload;
        },
        clearSignupNiasData: state => {
            state.signupNiasData = null;
        },
    },
});

const { actions } = signInSlice;

export const {
    signInError,
    signOut,
    signInRequest,
    signInSuccess,
    signInDone,
    tokenRenewError,
    tokenRenewRequest,
    tokenRenewSuccess,
    changeShowChangeEntityDialog,
    setSignupNiasData,
    clearSignupNiasData,
} = actions;

export const signInReducer = signInSlice.reducer;
